import { Injectable, signal, computed, inject } from '@angular/core';
import { UserEntity } from '../../domain/entities/user.entity';
import { LocalStorageProvider } from '../../../core/providers/storage/local-storage.provider';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LoggerProvider } from '@shared';

const ACCESS_TOKEN_KEY = 'cm_access_token';
const USER_KEY = 'cm_user';

/**
 * Auth state management using Angular signals.
 * Manages authentication state including user data, tokens, and loading states.
 * Persists to localStorage for session persistence across page reloads.
 */
@Injectable({ providedIn: 'root' })
export class AuthState {
  private readonly localStorage = inject(LocalStorageProvider);
  private readonly jwtHelperService = new JwtHelperService();
  private readonly logger = inject(LoggerProvider).createScoped('AuthState');

  private readonly _user = signal<UserEntity | null>(this.localStorage.get<UserEntity>(USER_KEY));
  private readonly _accessToken = signal<string | null>(this.getValidAccessToken());
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  // Read-only signals
  public readonly user = this._user.asReadonly();
  public readonly accessToken = this._accessToken.asReadonly();
  public readonly loading = this._loading.asReadonly();
  public readonly error = this._error.asReadonly();

  // Computed signals
  public readonly isAuthenticated = computed(() => this._user() !== null && this._accessToken() !== null);
  public readonly userFullName = computed(() => {
    const user = this._user();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });

  /**
   * Retrieves the access token from localStorage and validates its expiration.
   * If the token is expired, it is automatically cleared from storage.
   * @returns The valid access token or null if expired/missing
   */
  private getValidAccessToken(): string | null {
    const token = this.localStorage.get<string>(ACCESS_TOKEN_KEY);

    if (!token) {
      return null;
    }

    try {
      // Decode token to inspect expiration time
      const decoded = this.jwtHelperService.decodeToken(token);
      if (!decoded) {
        this.logger.warn('Token decoding returned null - invalid token format');
        this.localStorage.remove(ACCESS_TOKEN_KEY);
        this.localStorage.remove(USER_KEY);
        return null;
      }

      const expiresAt = decoded.exp ? new Date(decoded.exp * 1000) : 'unknown';
      const nowUtc = Date.now();
      const tokenExpiresUtc = decoded.exp ? decoded.exp * 1000 : 0;
      const secondsUntilExpiry = (tokenExpiresUtc - nowUtc) / 1000;

      // Check if token is actually expired (no buffer - token must still be valid)
      // Using the helper with offsetSeconds=0 means strictly check exp > now
      if (this.jwtHelperService.isTokenExpired(token, 0)) {
        this.logger.info('Token has expired', { expiresAt, secondsUntilExpiry });
        // Token is expired, clear it from storage
        this.localStorage.remove(ACCESS_TOKEN_KEY);
        this.localStorage.remove(USER_KEY); // Also clear user data for consistency
        return null;
      }

      this.logger.debug('Token validated successfully', {
        expiresAt,
        secondsUntilExpiry: Math.round(secondsUntilExpiry),
      });
      return token;
    } catch (error) {
      // Invalid token format, clear it
      this.logger.error('Invalid token format, clearing auth state', error);
      this.localStorage.remove(ACCESS_TOKEN_KEY);
      this.localStorage.remove(USER_KEY);
      return null;
    }
  }

  /**
   * Sets the authenticated user.
   * @param user - The user entity
   */
  public setUser(user: UserEntity | null): void {
    this._user.set(user);
    if (user) {
      this.localStorage.set(USER_KEY, user);
    } else {
      this.localStorage.remove(USER_KEY);
    }
  }

  /**
   * Sets the access token.
   * @param token - The access token
   */
  public setAccessToken(token: string | null): void {
    this._accessToken.set(token);
    if (token) {
      this.localStorage.set(ACCESS_TOKEN_KEY, token);
    } else {
      this.localStorage.remove(ACCESS_TOKEN_KEY);
    }
  }

  /**
   * Sets the loading state.
   * @param loading - Loading state
   */
  public setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  /**
   * Sets an error message.
   * @param error - Error message
   */
  public setError(error: string | null): void {
    this._error.set(error);
    this._loading.set(false);
  }

  /**
   * Clears all authentication state.
   */
  public clear(): void {
    this._user.set(null);
    this._accessToken.set(null);
    this._loading.set(false);
    this._error.set(null);
    this.localStorage.remove(USER_KEY);
    this.localStorage.remove(ACCESS_TOKEN_KEY);
  }
}
